import { OrganizerQuotaCard } from './OrganizerQuotaCard';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent } from '@tark/shared/components';
import {
  useAuth,
  useLockOrganizerCompetitionEntrants,
  useMergeOrganizerCompetitionEntrants,
  useOrganizerCompetitionEntrants,
  useOrganizerCompetitions,
  useReplaceOrganizerCompetitionEntry,
  useReviewOrganizerCompetitionEntry,
  useTenantBootstrap,
} from '@tark/shared/hooks';
import { getErrorMessage, type CompetitionOrganizerEntry } from '@tark/shared/lib';
import { AlertCircle, CheckCircle2, LockKeyhole, Loader2, RefreshCw, Trophy } from 'lucide-react';
import CoachPageHeader from '../../components/CoachPageHeader';
import OrganizerActionDialog from './organizer/OrganizerActionDialog';
import OrganizerEntrantFilters from './organizer/OrganizerEntrantFilters';
import OrganizerEntrantTable from './organizer/OrganizerEntrantTable';
import OrganizerDrawPanel from './organizer/OrganizerDrawPanel';
import OrganizerLaunchPanel from './organizer/OrganizerLaunchPanel';
import type {
  OrganizerDialogSubmission,
  OrganizerEntryIntent,
  OrganizerFilters,
} from './organizer/organizerCompetitionUi';
import {
  filterOrganizerEntries,
  formatCompetitionCode,
  formatCompetitionDate,
  organizerLockHint,
  uniqueLinkedEntrants,
} from './organizer/organizerCompetitionUi';

const REVIEW_ROLES = ['OWNER', 'ADMIN', 'STAFF'];
const ADMIN_ROLES = ['OWNER', 'ADMIN'];
const initialFilters: OrganizerFilters = { search: '', divisionPublicId: '', status: '' };

export default function OrganizerCompetitionsPage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(initialFilters);
  const [intent, setIntent] = useState<OrganizerEntryIntent | null>(null);
  const [actionEntryId, setActionEntryId] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [pageMessage, setPageMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const roleCode = activeTenant?.roleCode?.toUpperCase() || '';
  const isOrganizerWorkspace =
    activeTenant?.tenant?.tenantArchetype === 'EVENT_OPERATOR' &&
    activeTenant?.tenant?.workSurface === 'EVENTS';
  const canReview = REVIEW_ROLES.includes(roleCode);
  const canAdmin = ADMIN_ROLES.includes(roleCode);
  const collection = useOrganizerCompetitions(Boolean(user) && isOrganizerWorkspace && canReview);

  const requestedCompetitionId = searchParams.get('competition');
  const selectedCompetition = useMemo(() => {
    const competitions = collection.data?.competitions ?? [];
    return competitions.find((competition) => competition.competitionPublicId === requestedCompetitionId)
      ?? competitions[0]
      ?? null;
  }, [collection.data?.competitions, requestedCompetitionId]);

  useEffect(() => {
    if (!selectedCompetition || requestedCompetitionId === selectedCompetition.competitionPublicId) return;
    setSearchParams({ competition: selectedCompetition.competitionPublicId }, { replace: true });
  }, [requestedCompetitionId, selectedCompetition, setSearchParams]);

  useEffect(() => {
    setFilters(initialFilters);
    setIntent(null);
    setDialogError(null);
    setPageMessage(null);
  }, [selectedCompetition?.competitionPublicId]);

  const isPilot = selectedCompetition?.leagueType === 'DOUBLES_PILOT';
  const queue = useOrganizerCompetitionEntrants(
    selectedCompetition?.competitionPublicId,
    Boolean(user) && isOrganizerWorkspace && canReview && !isPilot
  );
  const entries = queue.data?.entries ?? [];
  const filteredEntries = useMemo(() => filterOrganizerEntries(entries, filters), [entries, filters]);
  const reviewEntry = useReviewOrganizerCompetitionEntry(selectedCompetition?.competitionPublicId);
  const replaceEntry = useReplaceOrganizerCompetitionEntry(selectedCompetition?.competitionPublicId);
  const mergeEntrants = useMergeOrganizerCompetitionEntrants(selectedCompetition?.competitionPublicId);
  const lockEntrants = useLockOrganizerCompetitionEntrants(selectedCompetition?.competitionPublicId);
  const dialogPending = reviewEntry.isPending || replaceEntry.isPending || mergeEntrants.isPending || lockEntrants.isPending;

  if (!isOrganizerWorkspace || !canReview) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Trophy className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-primary">Organizer Studio</p>
              <h1 className="mt-2 text-2xl font-semibold text-text">Competition operations need an organizer workspace</h1>
              <p className="mt-2 text-sm text-text-muted">Switch to an Event Operator workspace with an active organizer role.</p>
            </div>
            <Button onClick={() => navigate('/welcome')}>Open Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (collection.isLoading) return <LoadingState label="Loading competitions" />;

  if (collection.isError) {
    return <ErrorState title="Competitions unavailable" onRetry={() => void collection.refetch()} />;
  }

  if (!selectedCompetition) {
    return (
      <div className="w-full space-y-6 pb-24 md:pb-8">
        <CoachPageHeader
          eyebrow="Organizer Studio"
          title="Competitions"
          subtitle="Published competitions will appear here when registration opens."
          meta={<WorkspacePill name={activeTenant?.tenant?.displayName || 'Organizer Studio'} role={roleCode} />}
        />
        <OrganizerQuotaCard tenantId={activeTenant!.tenant!.id} />
        {isOrganizerWorkspace && ['OWNER','ADMIN'].includes(roleCode) && <Button onClick={() => navigate('/organizer/leagues')}>Event setup</Button>}
        <Card><CardContent className="py-14 text-center"><p className="font-semibold text-text">No competitions yet</p></CardContent></Card>
      </div>
    );
  }

  const lockHint = organizerLockHint(selectedCompetition, entries);
  const readOnly = Boolean(selectedCompetition.entrantsLockedAt || queue.data?.entrantsLockedAt);

  const handleReview = async (entry: CompetitionOrganizerEntry, action: 'approve' | 'reserve') => {
    setActionEntryId(entry.entryPublicId);
    setPageMessage(null);
    try {
      await reviewEntry.mutateAsync({
        entryPublicId: entry.entryPublicId,
        action,
        request: { clientMutationId: createMutationId(), expectedVersion: entry.version },
      });
      setPageMessage({ tone: 'success', text: action === 'approve' ? 'Entrant approved.' : 'Entrant moved to reserve.' });
    } catch (error) {
      setPageMessage({ tone: 'error', text: organizerErrorMessage(error) });
      void queue.refetch();
    } finally {
      setActionEntryId(null);
    }
  };

  const handleDialogSubmit = async (submission: OrganizerDialogSubmission) => {
    if (!intent || !selectedCompetition) return;
    setDialogError(null);
    try {
      if (intent.kind === 'decline' || intent.kind === 'withdraw') {
        await reviewEntry.mutateAsync({
          entryPublicId: intent.entry.entryPublicId,
          action: intent.kind,
          request: { clientMutationId: createMutationId(), expectedVersion: intent.entry.version, reason: submission.reason },
        });
      } else if (intent.kind === 'replace') {
        await replaceEntry.mutateAsync({
          entryPublicId: intent.entry.entryPublicId,
          request: {
            clientMutationId: createMutationId(),
            expectedVersion: intent.entry.version,
            outgoingEntrantPublicId: submission.outgoingEntrantPublicId!,
            replacementEntrantPublicId: submission.targetEntrantPublicId!,
            reason: submission.reason,
          },
        });
      } else if (intent.kind === 'merge') {
        const target = uniqueLinkedEntrants(entries).find(
          (entrant) => entrant.entrantPublicId === submission.targetEntrantPublicId
        );
        if (!target) throw new Error('The selected linked player is no longer available.');
        await mergeEntrants.mutateAsync({
          clientMutationId: createMutationId(),
          sourceEntrantPublicId: intent.source.entrantPublicId,
          expectedSourceVersion: intent.source.version,
          canonicalEntrantPublicId: target.entrantPublicId,
          expectedCanonicalVersion: target.version,
          reason: submission.reason,
        });
      } else if (intent.kind === 'lock') {
        await lockEntrants.mutateAsync({
          clientMutationId: createMutationId(),
          expectedVersion: intent.competitionVersion,
          reason: submission.reason || undefined,
        });
      }
      setIntent(null);
      setPageMessage({ tone: 'success', text: successMessageForIntent(intent) });
    } catch (error) {
      setDialogError(organizerErrorMessage(error));
      void queue.refetch();
    }
  };

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <OrganizerQuotaCard tenantId={activeTenant!.tenant!.id} />
      {isOrganizerWorkspace && ['OWNER','ADMIN'].includes(roleCode) && <Button onClick={() => navigate('/organizer/leagues')}>Event setup</Button>}
      <CoachPageHeader
        eyebrow="Organizer Studio"
        title={selectedCompetition.name}
        subtitle={isPilot ? "Manage team registrations through league event setup." : "Review the field, resolve exceptions, and lock entrants when registration is complete."}
        meta={<WorkspacePill name={activeTenant?.tenant?.displayName || 'Organizer Studio'} role={roleCode} />}
        action={
          <div className="flex w-full min-w-0 flex-wrap items-end gap-2 sm:w-auto">
            <label className="w-full min-w-0 space-y-1 text-left sm:w-auto">
              <span className="text-xs font-semibold text-text-muted">Competition</span>
              <select
                aria-label="Competition"
                className="h-10 w-full min-w-0 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 sm:w-auto sm:min-w-56"
                value={selectedCompetition.competitionPublicId}
                onChange={(event) => setSearchParams({ competition: event.target.value })}
              >
                {collection.data?.competitions.map((competition) => (
                  <option key={competition.competitionPublicId} value={competition.competitionPublicId}>{competition.name}</option>
                ))}
              </select>
            </label>
            {canAdmin && !isPilot ? (
              <Button
                leftIcon={<LockKeyhole className="h-4 w-4" />}
                disabled={!lockHint.ready || !queue.data}
                title={lockHint.text}
                onClick={() => setIntent({
                  kind: 'lock',
                  competition: selectedCompetition,
                  competitionVersion: queue.data?.competitionVersion ?? 0,
                })}
              >
                Lock entrants
              </Button>
            ) : null}
          </div>
        }
        stats={isPilot ? undefined :
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MiniStat label="Pending" value={selectedCompetition.pendingEntryCount} />
            <MiniStat label="Approved" value={selectedCompetition.approvedEntryCount} />
            <MiniStat label="Reserve" value={selectedCompetition.reserveEntryCount} />
            <MiniStat label="Divisions" value={selectedCompetition.divisionCount} />
          </div>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-text-muted">
        <p>
          Registration closes {formatCompetitionDate(selectedCompetition.registrationClosesAt, selectedCompetition.timezone)}
          {!isPilot && <>{' · '}{lockHint.text}</>}
        </p>
        <Button size="sm" variant="ghost" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => void (isPilot ? collection.refetch() : Promise.all([collection.refetch(), queue.refetch()]))}>
          Refresh
        </Button>
      </div>

      {pageMessage ? <InlineMessage tone={pageMessage.tone} text={pageMessage.text} /> : null}

      {isPilot ? (
        <Card><CardContent className="space-y-3">
          <p>Classic draw and scheduling are not available for pilot leagues.</p>
          <Link className="inline-flex min-h-11 items-center font-semibold text-primary underline" to={`/organizer/leagues/${selectedCompetition.competitionPublicId}`}>Manage league</Link>
        </CardContent></Card>
      ) : <>
      <OrganizerLaunchPanel
        competitionPublicId={selectedCompetition.competitionPublicId}
        canAdmin={canAdmin}
      />

      <OrganizerDrawPanel
        competitionPublicId={selectedCompetition.competitionPublicId}
        entrantsLocked={readOnly}
        canAdmin={canAdmin}
      />

      <Card padding="none" className="overflow-visible">
        <OrganizerEntrantFilters filters={filters} entries={entries} onChange={setFilters} />
        {queue.isLoading ? (
          <LoadingState label="Loading entrants" compact />
        ) : queue.isError ? (
          <ErrorState title="Entrants unavailable" onRetry={() => void queue.refetch()} compact />
        ) : (
          <OrganizerEntrantTable
            entries={filteredEntries}
            timeZone={selectedCompetition.timezone}
            canAdmin={canAdmin}
            readOnly={readOnly}
            actionEntryId={actionEntryId}
            onReview={(entry, action) => void handleReview(entry, action)}
            onIntent={(nextIntent) => { setDialogError(null); setIntent(nextIntent); }}
          />
        )}
      </Card>

      <OrganizerActionDialog
        intent={intent}
        entries={entries}
        isPending={dialogPending}
        errorMessage={dialogError}
        onClose={() => { if (!dialogPending) { setIntent(null); setDialogError(null); } }}
        onSubmit={(submission) => void handleDialogSubmit(submission)}
      />
      </>}
    </div>
  );
}

function WorkspacePill({ name, role }: { name: string; role: string }) {
  return <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">{name} · {formatCompetitionCode(role)}</span>;
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return <div><p className="text-xs font-medium text-text-muted">{label}</p><p className="mt-1 text-xl font-semibold text-text">{value}</p></div>;
}

function LoadingState({ label, compact = false }: { label: string; compact?: boolean }) {
  return <div className={`flex items-center justify-center gap-2 text-sm text-text-muted ${compact ? 'min-h-48' : 'min-h-[360px]'}`}><Loader2 className="h-5 w-5 animate-spin text-primary" />{label}</div>;
}

function ErrorState({ title, onRetry, compact = false }: { title: string; onRetry: () => void; compact?: boolean }) {
  return <div className={`flex flex-col items-center justify-center gap-3 text-center ${compact ? 'min-h-48 p-4' : 'min-h-[360px]'}`}><AlertCircle className="h-8 w-8 text-red-500" /><div><p className="font-semibold text-text">{title}</p><p className="mt-1 text-sm text-text-muted">Refresh to restore the latest organizer data.</p></div><Button variant="outline" onClick={onRetry}>Retry</Button></div>;
}

function InlineMessage({ tone, text }: { tone: 'success' | 'error'; text: string }) {
  const Icon = tone === 'success' ? CheckCircle2 : AlertCircle;
  return <div role={tone === 'error' ? 'alert' : 'status'} className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${tone === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700' : 'border-red-500/30 bg-red-500/10 text-red-700'}`}><Icon className="h-4 w-4" />{text}</div>;
}

function createMutationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function organizerErrorMessage(error: unknown): string {
  const message = getErrorMessage(error).toLowerCase();
  if (message.includes('version') || message.includes('changed') || message.includes('stale')) return 'This registration changed while you were reviewing it. The latest field is loading now.';
  if (message.includes('capacity') || message.includes('full')) return 'That division is full. Refresh the field and use reserve when appropriate.';
  if (message.includes('registration') || message.includes('locked') || message.includes('closed')) return 'Registration state no longer allows that action. Refresh to see the current field.';
  if (message.includes('selected linked player')) return 'The selected linked player is no longer available. Refresh and choose again.';
  return 'We could not complete that organizer action. Refresh and try again.';
}

function successMessageForIntent(intent: OrganizerEntryIntent): string {
  switch (intent.kind) {
    case 'decline': return 'Entrant declined.';
    case 'withdraw': return 'Entry withdrawn.';
    case 'replace': return 'Entrant replaced.';
    case 'merge': return 'Duplicate entrant merged.';
    case 'lock': return 'Entrants locked. The field is ready for draw preparation.';
  }
}
